/**
 * EmploymentRolesManagementPage — list, search, filter employment roles.
 * Standard management page pattern.
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  ChevronRight,
  CheckCircle,
  ListChecks,
  ClipboardList,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import { useDeleteEmploymentRole } from '../hooks';
import { localizedName } from '@/utils/localized';
import type { EmploymentRole, EmploymentRoleListResponse } from '../types';
import { EmploymentRoleFormModal } from './EmploymentRoleFormModal';
import { EmploymentRoleDetailModal } from './EmploymentRoleDetailModal';
import { useModal } from '@/components/modal';
import { GlassCard } from '@/components/glass';
import toast from 'react-hot-toast';
import { PageHeader, StatCard, CardSkeleton, EmptyState, ErrorState, SearchInput, GlassSelect, CreateButton, BulkActionBar, StatusDot, InfiniteScrollSentinel } from '@/components/ui';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useRTL } from '@/hooks/useRTL';
import { useSelectionSet } from '@/hooks/useSelectionSet';

const PAGE_SIZE = 20;

export function EmploymentRolesManagementPage() {
  const { t, i18n } = useTranslation();
  const isRTL = useRTL();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editRole, setEditRole] = useState<EmploymentRole | null>(null);
  const [selectedRole, setSelectedRole] = useState<EmploymentRole | null>(null);

  const queryClient = useQueryClient();
  const deleteMutation = useDeleteEmploymentRole();
  const { openConfirm } = useModal();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['employment-roles', 'infinite', search, statusFilter],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, unknown> = {
        page: pageParam,
        limit: PAGE_SIZE,
      };
      if (search) params.search = search;
      if (statusFilter) params.isActive = statusFilter;

      const { data } = await http.get<EmploymentRoleListResponse>('/employment-roles', { params });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.employmentRoles.length, 0);
      return loaded < lastPage.pagination.total ? allPages.length + 1 : undefined;
    },
  });

  const sentinelRef = useInfiniteScroll({ fetchNextPage, hasNextPage, isFetchingNextPage });

  const allRoles = useMemo(
    () => data?.pages.flatMap((page) => page.employmentRoles) || [],
    [data]
  );

  const { selectedIds, isSelected, toggle, selectAll, clear, count, allSelected } = useSelectionSet(allRoles);

  const totalCount = data?.pages[0]?.pagination.total || 0;
  const activeCount = useMemo(
    () => allRoles.filter((r) => r.isActive).length,
    [allRoles]
  );
  const withRatesCount = useMemo(
    () => allRoles.filter((r) => (r._count?.rates ?? 0) > 0).length,
    [allRoles]
  );

  const handleBulkDelete = () => {
    triggerHapticFeedback();
    openConfirm({
      title: t('employmentRoles.bulkDeleteTitle'),
      message: t('employmentRoles.bulkDeleteMessage', { count }),
      variant: 'danger',
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        await Promise.all(Array.from(selectedIds).map(id => deleteMutation.mutateAsync(id)));
        toast.success(t('employmentRoles.bulkDeletedSuccess'));
        clear();
      },
    });
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-4 md:space-y-6 pb-24"
    >
      {/* Header */}
      <motion.div variants={staggerItem}>
        <PageHeader
          icon={<Briefcase className="w-7 h-7" />}
          title={t('employmentRoles.management')}
          action={
            <CreateButton
              label={t('employmentRoles.create')}
              onClick={() => {
                triggerHapticFeedback();
                setCreateModalOpen(true);
              }}
            />
          }
        />
      </motion.div>

      {/* Stats Cards */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<Briefcase className="w-7 h-7 md:w-8 md:h-8" />} label={t('employmentRoles.total')} value={totalCount} />
        <StatCard icon={<CheckCircle className="w-7 h-7 md:w-8 md:h-8" />} label={t('employmentRoles.active')} value={activeCount} />
        <StatCard icon={<ListChecks className="w-7 h-7 md:w-8 md:h-8" />} label={t('employmentRoles.rates.title')} value={withRatesCount} />
      </motion.div>

      {/* Search & Filters */}
      <motion.div variants={staggerItem}>
        <GlassCard title={t('common.filters')}>
        <div className="flex flex-col md:flex-row gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder={t('employmentRoles.searchPlaceholder')} />

          <button onClick={selectAll} className="flex items-center gap-2 px-3 py-2 border border-white/30 rounded-lg bg-white/20 cursor-pointer hover:bg-white/20 transition-colors">
            <input type="checkbox" checked={allSelected} onChange={selectAll} className="w-5 h-5 pointer-events-none" readOnly />
            <span className="font-bold text-sm">{t('employmentRoles.selectAll')}</span>
          </button>

          <GlassSelect
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              triggerHapticFeedback();
            }}
          >
            <option value="">{t('employmentRoles.allStatuses')}</option>
            <option value="true">{t('employmentRoles.active')}</option>
            <option value="false">{t('employmentRoles.inactive')}</option>
          </GlassSelect>
        </div>
        </GlassCard>
      </motion.div>

      {/* List */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </motion.div>
        ) : isError ? (
          <ErrorState message={t('employmentRoles.loadError')} />
        ) : allRoles.length === 0 ? (
          <EmptyState icon={<Briefcase className="w-14 h-14 md:w-16 md:h-16" />} message={t('employmentRoles.noRoles')} />
        ) : (
          <motion.div
            key="content"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {allRoles.map((role) => {
              const selected = isSelected(role.id);

              return (
              <motion.div
                key={role.id}
                role="button"
                tabIndex={0}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  triggerHapticFeedback();
                  setSelectedRole(role);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    triggerHapticFeedback();
                    setSelectedRole(role);
                  }
                }}
                className="flex items-center justify-between w-full py-1.5 ps-6 pe-4 md:py-2 md:ps-6 md:pe-4 rounded-xl text-start cursor-pointer glass-surface"
                style={{
                  backgroundColor: ADMIN_COLORS.surface,
                  border: `1px solid ${ADMIN_COLORS.border}`,
                  boxShadow: selected ? ADMIN_COLORS.shadowMd : ADMIN_COLORS.shadow,
                }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Checkbox */}
                  <div
                    className="flex-shrink-0 flex items-center justify-center cursor-pointer"
                    style={{ minWidth: '44px', minHeight: '44px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(role.id);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggle(role.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-5 h-5 pointer-events-none"
                    />
                  </div>

                  <div
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{
                      backgroundColor: role.isActive ? '#ecfdf5' : '#f3f4f6',
                      border: `1px solid ${ADMIN_COLORS.border}`,
                    }}
                  >
                    <Briefcase className="w-5 h-5 md:w-6 md:h-6 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-neutral-800 truncate">
                      {localizedName(role, i18n.language)}
                    </p>
                    {role._count && (
                      <div className="flex items-center gap-2.5 mt-0.5 text-xs text-slate-500">
                        <span className="flex items-center gap-0.5">
                          <ClipboardList className="w-3 h-3" />
                          {role._count.rates} {t('employmentRoles.rates.title')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <StatusDot
                    active={role.isActive}
                    title={role.isActive ? t('employmentRoles.active') : t('employmentRoles.inactive')}
                  />
                  <ChevronRight
                    className="w-4 h-4 text-slate-400"
                    style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
                  />
                </div>
              </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Infinite scroll sentinel */}
      <InfiniteScrollSentinel hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} sentinelRef={sentinelRef} />

      {/* Modals */}
      <EmploymentRoleFormModal
        isOpen={createModalOpen || !!editRole}
        onClose={() => {
          queryClient.refetchQueries({ queryKey: ['employment-roles'] });
          setCreateModalOpen(false);
          setEditRole(null);
        }}
        editData={editRole}
      />

      {selectedRole && (
        <EmploymentRoleDetailModal
          employmentRole={selectedRole}
          onClose={() => { queryClient.refetchQueries({ queryKey: ['employment-roles'] }); setSelectedRole(null); }}
          onEdit={(r) => {
            setSelectedRole(null);
            setEditRole(r);
          }}
        />
      )}

      {/* Bulk Action Bar */}
      <BulkActionBar count={count} onDelete={handleBulkDelete} onClear={clear} isDeleting={deleteMutation.isPending} />
    </motion.div>
  );
}
